import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import QuizComponent from '../../components/QuizComponent';
import { ArrowRight, Heart, Flame, Scale, Hand, RefreshCw } from 'lucide-react';

const whatNowContent = {
  sections: [
    {
      id: 'offer-not-strive',
      title: 'Offer, Don\'t Strive',
      icon: Hand,
      content: `The Christian life is not about trying harder. It's about trusting Christ's life in you.

"I appeal to you therefore, brothers, by the mercies of God, to present your bodies as a living sacrifice, holy and acceptable to God, which is your spiritual worship." (Romans 12:1 ESV)

Notice: Paul says PRESENT (offer), not PRODUCE. You're not manufacturing holiness—you're offering yourself to the One who IS holy. It's not "try harder to be good." It's "trust the Good One living in you."

"I have been crucified with Christ. It is no longer I who live, but Christ who lives in me. And the life I now live in the flesh I live by faith in the Son of God, who loved me and gave himself for me." (Galatians 2:20 ESV)

Christ lives IN you. Your job isn't to produce Christian behavior—it's to trust His life expressing itself through you. When you strive in your own strength, you cut yourself off from His power. When you rest in Him, His life flows.

**The switch:**
• Old: "I need to try harder to be holy"
• New: "I trust Christ's life in me"

**Key Anchors:**
• Romans 12:1 – "Present your bodies"
• Galatians 2:20 – "Christ lives in me"
• Colossians 1:27 – "Christ in you, the hope of glory"`
    },
    {
      id: 'guilt-to-gratitude',
      title: 'From Guilt to Gratitude',
      icon: RefreshCw,
      content: `When you sin, what's your first response? For many Christians, it's: "God, please forgive me."

But you're already forgiven. All trespasses. Past, present, future. At the cross. (Colossians 2:13-14)

The guilt-based response reinforces a lie—that you've lost something, that you need to be re-cleansed, that fellowship is broken. But none of that is true under the New Covenant.

**The switch:**
• Old: "God, forgive me. I'm so sorry. Please cleanse me."
• New: "Father, thank You that I'm already forgiven. That wasn't the real me. I rest in Your finished work and trust Your life in me."

This isn't license to sin—it's freedom FROM sin's power. Guilt keeps you focused on yourself and your failure. Gratitude keeps you focused on Christ and His victory.

"There is therefore now no condemnation for those who are in Christ Jesus." (Romans 8:1 ESV)

When the Spirit convicts you of sin, He's not condemning you. He's reminding you: "That's not who you are. You're better than that. Trust Me."

**Key Anchors:**
• Romans 8:1 – "No condemnation"
• Ephesians 1:7 – "We HAVE forgiveness"
• Colossians 2:13-14 – "ALL trespasses forgiven"`
    },
    {
      id: 'grace-trains',
      title: 'Grace Trains Us',
      icon: Flame,
      content: `Here's the paradox that religious thinking can't grasp: grace—not law—produces holiness.

"For the grace of God has appeared, bringing salvation for all people, training us to renounce ungodliness and worldly passions, and to live self-controlled, upright, and godly lives in the present age." (Titus 2:11-12 ESV)

Grace TRAINS us. Not guilt. Not fear. Not rules. Grace itself.

And here's why law doesn't work:

"For while we were living in the flesh, our sinful passions, aroused by the law, were at work in our members to bear fruit for death." (Romans 7:5 ESV)

The law AROUSES sinful passions. Tell someone "don't think about pink elephants" and what do they think about? The flesh responds to law with rebellion. This is why rule-keeping Christians often struggle MORE than grace-trusting Christians.

"For sin will have no dominion over you, since you are not under law but under grace." (Romans 6:14 ESV)

Want freedom from sin's power? Get OUT from under law and REST in grace. Grace doesn't produce license—it produces genuine transformation from the inside out.

**Key Anchors:**
• Titus 2:11-12 – "Grace... training us"
• Romans 7:5 – "Sinful passions aroused by the law"
• Romans 6:14 – "Not under law but under grace"`
    },
    {
      id: 'hearing-spirit',
      title: 'Hearing the Spirit: Assurance, Not Condemnation',
      icon: Heart,
      content: `How do you know when the Spirit is speaking vs. when it's just guilt or the enemy?

Simple test: The Spirit leads to ASSURANCE. Accusation leads to condemnation.

"For you did not receive the spirit of slavery to fall back into fear, but you have received the Spirit of adoption as sons, by whom we cry, 'Abba! Father!' The Spirit himself bears witness with our spirit that we are children of God." (Romans 8:14-16 ESV)

The Spirit's voice:
• Reminds you of your identity ("You're a child of God")
• Produces assurance, not fear
• Points to Christ's finished work
• Leads you to rest and trust

The accuser's voice:
• Emphasizes your failure
• Produces guilt, shame, fear
• Points to your performance
• Leads to striving or despair

When you sin and feel conviction, the Spirit isn't saying "You're bad and God is disappointed." He's saying "That's not who you are—you're better than that. Trust Me."

**Key Anchors:**
• Romans 8:14-16 – "Spirit of adoption, not slavery to fear"
• 1 John 4:18 – "Perfect love casts out fear"
• John 16:8-11 – Spirit convicts world of sin, righteousness, judgment—not believers of their failures`
    },
    {
      id: 'legalism-vs-holiness',
      title: 'Legalism vs. True Holiness',
      icon: Scale,
      content: `Legalism and holiness can look identical from the outside. Both involve "doing right things." But the roots and fruits are entirely different.

**LEGALISM:**
• Obeys to earn/maintain God's favor
• Focuses on behavior modification
• Produces pride (in success) or despair (in failure)
• Motivated by fear/guilt
• Results in exhaustion
• "I must do this or else..."

**TRUE HOLINESS:**
• Obeys because already fully accepted
• Flows from transformed identity
• Produces humble confidence
• Motivated by love/gratitude
• Results in rest
• "I get to live this way because of who I am"

"For sin will have no dominion over you, since you are not under law but under grace." (Romans 6:14 ESV)

Being under grace doesn't mean ignoring God's ways—it means living from a new position (accepted) with new power (the Spirit). The Pharisees weren't too holy; they weren't holy at all—just religious. Jesus calls for something deeper: transformation from the inside out.

**The test:** Does your "holiness" produce joy and rest, or anxiety and exhaustion? Grace-based holiness energizes. Law-based religion depletes.

**Key Anchors:**
• Romans 6:14 – "Not under law but under grace"
• Galatians 5:18 – "Led by the Spirit, not under law"
• Matthew 11:28-30 – "My yoke is easy, my burden is light"`
    }
  ]
};

export default function WhatNowHub() {
  return (
    <div className="min-h-[calc(100vh-64px)]" data-testid="whatnow-hub">
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1679499066019-c98cff381d81?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Lone tree in field representing the journey ahead"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hub-hero-overlay flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 w-full">
            <span className="inline-block px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm mb-3">
              Step 5 · Obedience
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              What Now?
            </h1>
            <p className="text-white/80 mt-2 max-w-2xl">
              Living from grace, not for grace
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="teaching" className="space-y-8">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="teaching" data-testid="tab-teaching">Teaching</TabsTrigger>
            <TabsTrigger value="quiz" data-testid="tab-quiz">Quiz</TabsTrigger>
          </TabsList>

          <TabsContent value="teaching" className="space-y-8">
            {/* Introduction */}
            <div className="max-w-3xl">
              <p className="text-lg text-muted-foreground leading-relaxed">
                You're forgiven. You're united. You're free. So... now what? Gospel living isn't about 
                checklists, striving, or trying harder. It's about trusting Christ in you and letting 
                grace—not guilt—train you.
              </p>
            </div>

            {/* Key Verse */}
            <Card className="max-w-3xl bg-primary/5 border-primary/20">
              <CardContent className="p-6">
                <p className="font-serif text-xl italic mb-2">
                  "For the grace of God has appeared... training us to renounce ungodliness and worldly 
                  passions, and to live self-controlled, upright, and godly lives."
                </p>
                <p className="text-primary font-medium">— Titus 2:11-12 ESV</p>
              </CardContent>
            </Card>

            {/* Teaching Sections */}
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                {whatNowContent.sections.map((section, index) => {
                  const Icon = section.icon;
                  return (
                    <AccordionItem 
                      key={section.id} 
                      value={section.id}
                      className="border rounded-lg px-6"
                    >
                      <AccordionTrigger className="hover:no-underline py-4">
                        <div className="flex items-center gap-3 text-left">
                          <span className="flex-shrink-0 p-2 rounded-lg bg-primary/10">
                            <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                          </span>
                          <span className="font-serif text-lg font-semibold">{section.title}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-6">
                        <div className="pl-14 prose prose-slate max-w-none">
                          {section.content.split('\n\n').map((paragraph, pIndex) => (
                            <p key={pIndex} className="text-foreground/80 leading-relaxed mb-4 last:mb-0 whitespace-pre-wrap">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>

            {/* Thanksgiving Trainer */}
            <Card className="max-w-3xl mx-auto">
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-primary" />
                  The Thanksgiving Switch
                </CardTitle>
                <CardDescription>Convert guilt responses to grace responses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
                      <p className="font-semibold text-destructive mb-2">Old Pattern (Guilt)</p>
                      <p className="text-sm">"God, I'm so sorry. Please forgive me. I'll try harder. Please don't be angry with me."</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="font-semibold text-green-700 mb-2">New Pattern (Grace)</p>
                      <p className="text-sm">"Father, thank You that I'm already forgiven. That wasn't the real me. I trust Your life in me."</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Why the switch matters:</strong> The guilt pattern keeps you focused on your failure 
                      and implies you've lost something. The grace pattern keeps you focused on Christ's 
                      victory and reminds you nothing has changed—you're still forgiven, still united, still loved.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="max-w-3xl mx-auto">
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <ArrowRight className="h-5 w-5 text-primary" />
                  The Grace-Living Cycle
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                    <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold flex-shrink-0">1</span>
                    <div>
                      <p className="font-semibold">Remember Who You Are</p>
                      <p className="text-sm text-muted-foreground">Forgiven. United. Complete. New. This is your identity—always.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                    <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold flex-shrink-0">2</span>
                    <div>
                      <p className="font-semibold">Trust Christ in You</p>
                      <p className="text-sm text-muted-foreground">Don't strive—trust. His life in you is the power source.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                    <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold flex-shrink-0">3</span>
                    <div>
                      <p className="font-semibold">Let Grace Train You</p>
                      <p className="text-sm text-muted-foreground">Not guilt. Not law. Grace produces transformation.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                    <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold flex-shrink-0">4</span>
                    <div>
                      <p className="font-semibold">When You Fall, Thank</p>
                      <p className="text-sm text-muted-foreground">Thank God you're forgiven. That wasn't the real you. Move on.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quiz">
            <div className="max-w-2xl mx-auto">
              <QuizComponent hubId="whatnow" />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
